import {
	Button,
	Checkbox,
	FormControlLabel,
	MuiThemeProvider,
	TextField,
	Tooltip,
	createMuiTheme,
} from "@material-ui/core";
import { Delete, Visibility } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import { SERVER_URL } from "../../../../api";
import {
	deleteResourceTypology,
	getResourceTypologyById,
	postResourceTypology,
	updateResourceTypology,
} from "../../../../api/resourceTypology";
import { checkIsEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { buttonsStyle } from "../../../components/tables/table";
import { useSkeleton } from "../../../hooks/useSkeleton";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyResourceTypology() {
	return {
		title: {},
		iconURL: null,
		active: true,
	};
}

export default function EditResourceTypologiesPage() {
	const [resourceTypology, setResourceTypology] = useState(
		getEmptyResourceTypology()
	);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [openPreviewImage, setOpenPreviewImage] = useState(false);

	const resourceTypologyId = useParams().id;
	const history = useHistory();
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!resourceTypologyId) {
			disableLoadingData();
			return;
		}
		getResourceTypologyById(resourceTypologyId)
			.then((res) => {
				if (res.status === 200) {
					setResourceTypology(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get resource typology.",
				});
				history.push("/resource-typologies");
			});
	}, [resourceTypologyId, disableLoadingData, history]);

	function saveResourceTypology() {
		if (checkIsEmpty(resourceTypology.title))
			return alertError({
				error: null,
				customMessage:
					"The title is required in at least one of the languages.",
			});
		if (!resourceTypologyId) {
			postResourceTypology(resourceTypology, selectedImage)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage:
								"Resource typology successfully created.",
						});
						history.push("/resource-typologies");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save resource typology.",
					});
				});
		} else {
			updateResourceTypology(
				resourceTypologyId,
				resourceTypology,
				selectedImage
			)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						history.push("/resource-typologies");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === " ") return;
		if (lang) {
			if (!resourceTypology[element]) resourceTypology[element] = {};
			let newText = resourceTypology[element];
			newText[lang] = event.target.value;
			setResourceTypology({ ...resourceTypology, [element]: newText });
		} else
			setResourceTypology({
				...resourceTypology,
				[element]: event.target.value,
			});
	};

	const renderMultilanguageTabContent = (lang) => {
		return (
			<>
				<br />
				<TextField
					id={`title`}
					label="Title"
					value={
						(resourceTypology.title &&
							resourceTypology.title[lang]) ||
						""
					}
					onChange={handleChange("title", lang)}
					InputLabelProps={{
						shrink: true,
					}}
					margin="normal"
					variant="outlined"
					required
				/>
			</>
		);
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit resource typology"></CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent={
								renderMultilanguageTabContent
							}
						/>
						<br />
						<label htmlFor={"upload-image"}>
							<input
								style={{ display: "none" }}
								id={"upload-image"}
								name={"upload-image"}
								type="file"
								accept={"image/*"}
								onChange={(e) => {
									setSelectedImage(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: "15px" }}
								color="secondary"
								component="span"
								variant="outlined"
							>
								{selectedImage || resourceTypology.iconURL
									? "Change image"
									: "Upload image"}
							</Button>
						</label>
						{(selectedImage || resourceTypology.iconURL) && (
							<>
								<Tooltip title={"Preview image"}>
									<Button
										size="small"
										onClick={() =>
											setOpenPreviewImage(true)
										}
										style={{
											...buttonsStyle,
										}}
									>
										<Visibility />
									</Button>
								</Tooltip>
								<Tooltip title="Delete">
									<Button
										size="small"
										style={{
											...buttonsStyle,
											marginRight: "15px",
										}}
										onClick={() => {
											setSelectedImage(null);
											setResourceTypology({
												...resourceTypology,
												iconURL: null,
											});
										}}
									>
										<Delete />
									</Button>
								</Tooltip>
								<PreviewDialog
									title={"Preview image"}
									open={openPreviewImage}
									setOpen={setOpenPreviewImage}
									src={
										selectedImage
											? URL.createObjectURL(selectedImage)
											: `${SERVER_URL}/${resourceTypology.iconURL}`
									}
								/>
								<span>
									{selectedImage
										? selectedImage?.name
										: resourceTypology.iconURL
										? resourceTypology.iconURL.split(
												/-(.*)/s
										  )[1]
										: ""}
								</span>
							</>
						)}
						<br />
						<br />
						<FormControlLabel
							control={
								<Checkbox
									checked={resourceTypology.active}
									onChange={() =>
										setResourceTypology({
											...resourceTypology,
											active: !resourceTypology.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>
					</CardBody>
				</Card>
				<div style={{ display: "flex", flexDirection: "row" }}>
					<Button
						onClick={() => history.push("/resource-typologies")}
						variant="outlined"
						style={{ marginRight: "20px" }}
					>
						Back
					</Button>
					<Button
						onClick={() => saveResourceTypology()}
						variant="outlined"
						color="primary"
						style={{ marginRight: "20px" }}
					>
						Save resource typology
					</Button>
					{resourceTypologyId && user?.role === "admin" && (
						<>
							<MuiThemeProvider theme={theme}>
								<Button
									onClick={() => setOpenConfirmDialog(true)}
									variant="outlined"
									color="secondary"
									style={{ marginRight: "20px" }}
								>
									Delete resource typology
								</Button>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										marginLeft: "auto",
									}}
								></div>
							</MuiThemeProvider>

							<ConfirmDialog
								title={
									"Are you sure you want to delete this resource typology?"
								}
								open={openConfirmDialog}
								setOpen={setOpenConfirmDialog}
								onConfirm={() => {
									deleteResourceTypology(resourceTypologyId)
										.then((res) => {
											if (
												res.status === 204 ||
												res.status === 200
											) {
												alertSuccess({
													title: "Deleted!",
													customMessage:
														"Resource typology deleted successfully",
												});
												history.push(
													"/resource-typologies"
												);
											}
										})
										.catch((error) => {
											alertError({
												error: error,
												customMessage:
													"Could not delete resource typology.",
											});
										});
								}}
							/>
						</>
					)}
				</div>
			</>
		);
}
